import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Stethoscope } from "lucide-react";

export default function ProfessionalRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    specialty: "", qualification: "", bio: "",
  });
  const [otp, setOtp] = useState("");
  const [stage, setStage] = useState("form"); // form | otp | done
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitForm = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/professional/register", form);
      toast.success("OTP sent to your email.");
      setStage("otp");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/professional/verify-otp", {
        email: form.email.trim().toLowerCase(), otp: otp.trim(),
      });
      toast.success("Email verified. Awaiting clinic approval.");
      setStage("done");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-16 md:py-20" data-testid="pro-register-page">
      <div className="bg-white rounded-3xl border border-[#EDF1EF] shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
        <span className="w-12 h-12 rounded-2xl bg-[#D9E4E1] flex items-center justify-center mb-5">
          <Stethoscope className="w-6 h-6 text-[#4A7065]" />
        </span>
        <h1 className="font-heading text-3xl text-slate-900">Join as a care provider</h1>
        <p className="text-sm text-slate-500 mt-1">
          For dietitians, fellow doctors and allied professionals. You'll be reviewed by Dr. Prathusha's team before going live.
        </p>

        {stage === "form" && (
          <form onSubmit={submitForm} className="mt-7 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pro-name">Full name</Label>
                <Input id="pro-name" data-testid="pro-reg-name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="pro-spec">Specialty</Label>
                <Input id="pro-spec" data-testid="pro-reg-specialty" required placeholder="e.g. Clinical Nutrition, Fertility" value={form.specialty} onChange={set("specialty")} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="pro-email">Email</Label>
                <Input id="pro-email" data-testid="pro-reg-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label htmlFor="pro-phone">Phone</Label>
                <Input id="pro-phone" data-testid="pro-reg-phone" inputMode="numeric" required value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pro-qual">Qualifications</Label>
                <Input id="pro-qual" data-testid="pro-reg-qual" placeholder="e.g. M.Sc. Clinical Nutrition, RD" value={form.qualification} onChange={set("qualification")} className="mt-1.5 rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pro-bio">Short bio (optional)</Label>
                <Textarea id="pro-bio" data-testid="pro-reg-bio" rows={3} value={form.bio} onChange={set("bio")} className="mt-1.5 rounded-xl" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="pro-pass">Password</Label>
                <Input id="pro-pass" data-testid="pro-reg-password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="mt-1.5 rounded-xl" />
              </div>
            </div>
            <Button data-testid="pro-reg-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6">
              {loading ? "Creating…" : "Create account & send OTP"}
            </Button>
          </form>
        )}

        {stage === "otp" && (
          <form onSubmit={submitOtp} className="mt-7 space-y-4">
            <p className="text-xs text-slate-500">We sent a 6-digit OTP to <b>{form.email}</b>. It expires in 10 minutes.</p>
            <div>
              <Label htmlFor="pro-otp">OTP</Label>
              <Input id="pro-otp" data-testid="pro-reg-otp" inputMode="numeric" required value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="mt-1.5 rounded-xl tracking-[0.5em] text-center" />
            </div>
            <Button data-testid="pro-reg-otp-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#5E8B7E] hover:bg-[#4A7065] py-6">
              {loading ? "Verifying…" : "Verify email"}
            </Button>
          </form>
        )}

        {stage === "done" && (
          <div data-testid="pro-reg-done" className="mt-7 rounded-xl border border-green-100 bg-green-50 p-5 text-sm text-green-800">
            <p className="font-semibold">You're all set!</p>
            <p className="mt-1.5">Your account is awaiting clinic approval. You'll receive an email once you're approved — then you can log in and start seeing patients.</p>
            <Button onClick={() => navigate("/professional/login")} className="mt-4 rounded-full bg-[#5E8B7E] hover:bg-[#4A7065]">Go to login</Button>
          </div>
        )}

        <p className="text-sm text-slate-500 text-center mt-6">
          Already a member?{" "}
          <Link to="/professional/login" data-testid="pro-reg-login-link" className="text-[#2C557E] font-medium hover:underline">
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
}
